/**
 * 一覧の表(antd Table のラッパ)。
 *
 * - `scroll.x = max-content` 既定: 列が収まらなければ表の中で横スクロール(外殻は動かない)
 * - 列幅は `width` 必須、`ellipsis` 既定(1 文字ずつの折り返しを起こさない)
 * - 行クリックで右パネル(`onRowClick`)。リンク・ボタン・チェックは行クリックにしない
 * - 件数と「N 件ずつ」は表の上の帯に。左は一括操作の置き場(`barLeft`)
 * - 空のときは `EmptyState`(一文 + 次の行動)。取得失敗(`error`)は空状態にしない
 *   (「まだ無い」と「取れなかった」は別。登録ボタンも出さない)
 * - セルの値が空(""/null/undefined)なら `emptyText`(既定 "—")で埋める(b1 低 8)。
 *   空セルが並ぶと「取れていない」のか「無い」のか分からなくなる
 * - `rowTone` で行の地色(期限超過の行など)。色は tone → `.ui-row--<tone>` の 1 か所
 * - `countLabel` で件数の表示を差し替える(手元で絞った一覧の「200 件中 N 件」。「全 N 件」と言わない)
 */
import { Select, Table, type TableProps } from "antd";
import type { ColumnType } from "antd/es/table";
import type { Key, MouseEvent, ReactNode } from "react";

import { EmptyState } from "./EmptyState";
import type { Tone } from "./StatusTag";

export type DataColumn<T> = Omit<ColumnType<T>, "width"> & { width: number };

const CLICK_GUARD = "a, button, input, label, .ant-checkbox, .ant-select, .ant-dropdown, .ant-tag, .ant-popover";

export function DataTable<T extends object>({
  columns,
  rows,
  rowKey,
  loading,
  total,
  page = 1,
  pageSize,
  defaultPageSize = 50,
  onPage,
  pageSizeOptions = [20, 50, 100, 200],
  unit = "件",
  countLabel,
  selectedKey,
  onRowClick,
  rowClickable,
  onSort,
  rowSelection,
  rowTone,
  sticky = false,
  expandable,
  barLeft,
  empty,
  emptyText = "—",
  error,
  size = "middle",
  scrollX = "max-content",
}: {
  columns: DataColumn<T>[];
  rows: T[];
  rowKey: (row: T) => Key;
  loading?: boolean;
  total: number;
  page?: number;
  /** 省略時は `defaultPageSize` */
  pageSize?: number;
  defaultPageSize?: number;
  onPage: (page: number, pageSize: number) => void;
  pageSizeOptions?: number[];
  unit?: string;
  /** 件数の表示(既定「全 N 件」)。取得が上限で切れた一覧では「200 件中 N 件」を渡す */
  countLabel?: ReactNode;
  selectedKey?: Key | null;
  onRowClick?: (row: T) => void;
  /** 行ごとに押せるか(既定はすべて押せる)。false の行は onRowClick を呼ばず、指の形にもしない */
  rowClickable?: (row: T) => boolean;
  /**
   * 列見出しの並び替え(`sorter: true` の列)。並べるのはサーバー側で、ここは
   * 「どの列をどちら向きに」を知らせるだけ。解除(3 回目)は dir=null
   */
  onSort?: (field: string, dir: "asc" | "desc" | null) => void;
  rowSelection?: TableProps<T>["rowSelection"];
  /** 行の地色(期限超過 = danger など)。undefined なら素の行 */
  rowTone?: (row: T) => Tone | undefined;
  /** ヘッダーを上バーの下に固定(長い一覧向け) */
  sticky?: boolean;
  expandable?: TableProps<T>["expandable"];
  barLeft?: ReactNode;
  empty: { text: ReactNode; action?: ReactNode };
  /** セルが空のときの埋め字 */
  emptyText?: ReactNode;
  /** 取得失敗の一文。あるときは空状態(と件数)を出さない。詳細は表の上の Alert で */
  error?: ReactNode;
  size?: "small" | "middle";
  /** 列幅の合計(選択列も含む)。指定時は長い文字列で表を広げない */
  scrollX?: number | "max-content";
}) {
  const size_ = pageSize ?? defaultPageSize;
  const cols = columns.map((c) => ({
    ellipsis: true,
    ...c,
    render: (v: unknown, row: T, i: number) => {
      const out = c.render ? c.render(v, row, i) : (v as ReactNode);
      return out === "" || out == null ? <span className="ui-cell-unset">{emptyText}</span> : out;
    },
  }));
  return (
    <div className="ui-table">
      <div className="ui-table-bar">
        <div className="ui-table-bar-left">{barLeft}</div>
        <div className="ui-table-bar-right">
          <span className="ui-num">
            {error ? `全 — ${unit}` : (countLabel ?? `全 ${total} ${unit}`)}
          </span>
          <span aria-hidden>·</span>
          <Select
            size="small"
            variant="borderless"
            value={size_}
            onChange={(n) => onPage(1, n)}
            options={pageSizeOptions.map((n) => ({ value: n, label: `${n} 件ずつ` }))}
            aria-label="1 ページの件数"
            popupMatchSelectWidth={false}
          />
        </div>
      </div>
      <Table<T>
        rowKey={rowKey}
        size={size}
        columns={cols}
        dataSource={rows}
        loading={loading}
        scroll={{ x: scrollX }}
        sticky={sticky ? { offsetHeader: 52 } : undefined}
        rowSelection={rowSelection}
        expandable={expandable}
        showSorterTooltip={false}
        onChange={(_p, _f, sorter, extra) => {
          // antd の onChange はページ送り・絞り込みでも呼ばれる。並び替えのときだけ親へ伝える
          // (ページを押すたびに onSort → 親が 1 ページ目へ戻し、2 ページ目以降へ行けなかった)
          if (!onSort || extra.action !== "sort") return;
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          // dataIndex の無い列(render だけの列)は columnKey で区別する
          const raw = s?.field != null ? s.field : s?.columnKey;
          const field = raw != null ? String(Array.isArray(raw) ? raw.join(".") : raw) : "";
          onSort(field, s?.order === "ascend" ? "asc" : s?.order === "descend" ? "desc" : null);
        }}
        rowClassName={(row) => {
          const tone = rowTone?.(row);
          return [
            onRowClick && (rowClickable?.(row) ?? true) ? "ui-row-clickable" : "",
            selectedKey != null && rowKey(row) === selectedKey ? "ui-row-selected" : "",
            tone ? `ui-row--${tone}` : "",
          ]
            .filter(Boolean)
            .join(" ");
        }}
        onRow={(row) => ({
          onClick: (e: MouseEvent<HTMLElement>) => {
            if (!onRowClick || !(rowClickable?.(row) ?? true)) return;
            if ((e.target as HTMLElement).closest(CLICK_GUARD)) return;
            onRowClick(row);
          },
        })}
        locale={{
          // 読み込み中は空状態(次の行動ボタン)を出さない。ヘッダーの主ボタンと二重に見えるため
          emptyText: loading ? (
            <div className="ui-empty ui-empty-loading">読み込み中…</div>
          ) : error ? (
            <div className="ui-empty ui-empty-error">{error}</div>
          ) : (
            <EmptyState text={empty.text} action={empty.action} />
          ),
        }}
        pagination={{
          current: page,
          pageSize: size_,
          total,
          showSizeChanger: false,
          hideOnSinglePage: true,
          size: "small",
          onChange: (p, s) => onPage(p, s),
        }}
      />
    </div>
  );
}
